import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

import { useBulkUpsertMortgageRulesMutation } from 'features/notification-config/services/businessRules.api';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import {
  MORTGAGE_PRE_EVALUATION_CONFIG,
  type MortgageBulkAction,
  type MortgageConfigurableRuleName,
  type MortgagePreEvaluationConfig,
  type ProjectRulesSnapshot,
  buildBulkRules,
  describeBulkChange,
  describeBulkPreEvaluation,
  idsToChange,
  parsePreEvaluationConfig,
  countWithoutMode,
  resolveBulkActions,
} from '../model/mortgageRules';
import MortgageAgentPromptsCard from './MortgageAgentPromptsCard';
import type { SubProjectPrompts } from './MortgageAgentPromptsCard';
import MortgageConfirmDialog from './MortgageConfirmDialog';
import MortgagePreEvaluationFields from './MortgagePreEvaluationFields';

interface Props {
  selectedIds: number[];
  selected: ProjectRulesSnapshot[];
  /** Every row of the table: the prompt card seeds its drafts from the selected ones. */
  subProjects: SubProjectPrompts[];
  countryCode: string | null | undefined;
  /** Seeds the pre-evaluation draft from the first selected project, so it is edited and not retyped. */
  seedConfig: MortgagePreEvaluationConfig | null;
  onApplied: () => void;
}

type Pending = { kind: 'rule'; name: MortgageConfigurableRuleName; value: boolean } | { kind: 'preEvaluation' };

/*
 * The section below the table: the table says where each sub-project stands, this says what to move.
 * Every rule is laid out the way the single-project screen lays it out, with its own description, and
 * the vertical decides which ones appear at all.
 */
const MortgageBulkActions = ({
  selectedIds,
  selected,
  subProjects,
  countryCode,
  seedConfig,
  onApplied,
}: Props): ReactElement => {
  const [bulkUpsert, { isLoading: isSaving }] = useBulkUpsertMortgageRulesMutation();
  const [draft, setDraft] = useState<MortgagePreEvaluationConfig>(seedConfig ?? parsePreEvaluationConfig(null));
  const [pending, setPending] = useState<Pending | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(seedConfig ?? parsePreEvaluationConfig(null));
  }, [seedConfig]);

  const hasSelection = selectedIds.length > 0;
  const actions = resolveBulkActions(selected);
  const withoutMode = countWithoutMode(selected);

  const actionBlock = (action: MortgageBulkAction): ReactElement => (
    <div
      key={action.name}
      className={cn('space-y-2 rounded-md border border-border p-3', !hasSelection && 'opacity-50')}
    >
      <div className="space-y-1">
        <p className="text-sm font-medium">{action.label}</p>
        <p className="whitespace-pre-line text-xs text-text-muted">{action.description}</p>
      </div>
      <div className="flex items-center gap-2">
        {action.canActivate && (
          <Button
            type="button"
            size="sm"
            disabled={!hasSelection || isSaving}
            onClick={() => setPending({ kind: 'rule', name: action.name, value: true })}
          >
            Activar
          </Button>
        )}
        {action.canDeactivate && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!hasSelection || isSaving}
            onClick={() => setPending({ kind: 'rule', name: action.name, value: false })}
          >
            Desactivar
          </Button>
        )}
      </div>
    </div>
  );

  const apply = async (rules: { name: string; value: unknown }[], entityIds = selectedIds): Promise<void> => {
    setError(null);

    try {
      await bulkUpsert({ entityIds, rules }).unwrap();
      setPending(null);
      onApplied();
    } catch (saveError) {
      setPending(null);
      setError(extractErrorMessage(saveError, 'No se pudo aplicar el cambio a los subproyectos seleccionados.'));
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (!pending) return;
    if (pending.kind === 'preEvaluation') {
      await apply([{ name: MORTGAGE_PRE_EVALUATION_CONFIG, value: draft }]);
      return;
    }
    await apply(buildBulkRules(pending.name, pending.value), idsToChange(pending.name, pending.value, selected));
  };

  const confirmCopy = ((): ReturnType<typeof describeBulkChange> | null => {
    if (!pending) return null;
    if (pending.kind === 'preEvaluation') return describeBulkPreEvaluation(selectedIds.length);
    return describeBulkChange(pending.name, pending.value, idsToChange(pending.name, pending.value, selected).length);
  })();

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-sm font-medium">Cambios en conjunto</h3>
        <p className="text-xs text-text-muted">
          {hasSelection
            ? `Se aplican a los ${selectedIds.length} subproyectos seleccionados en la tabla.`
            : 'Selecciona subproyectos en la tabla para aplicar un cambio sobre todos a la vez.'}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="flex flex-col space-y-3 rounded-md border border-border p-6">
          <p className="text-sm font-medium">Agentes</p>

          {actions.map(actionBlock)}

          {withoutMode > 0 && (
            <p className="text-xs text-text-muted">
              {`${withoutMode} de los seleccionados tienen el modo hipotecario desactivado, así que sus agentes no se ofrecen para el conjunto. Actívalo arriba para poder moverlos.`}
            </p>
          )}
        </section>

        <section className="flex flex-col space-y-3 rounded-md border border-border p-6">
          <div className={cn('space-y-1', !hasSelection && 'opacity-50')}>
            <p className="text-sm font-medium">Pre-evaluación</p>
            <p className="text-xs text-text-muted">
              Contra qué entidades financieras se evalúa a los clientes, si se envía el PDF de capacidad y si el
              proyecto corre con múltiples agentes. Reemplaza por completo lo que cada subproyecto seleccionado tenga
              hoy.
            </p>
          </div>

          <div className={cn(!hasSelection && 'opacity-50')}>
            <MortgagePreEvaluationFields
              countryCode={countryCode}
              value={draft}
              disabled={!hasSelection || isSaving}
              topology="subProjects"
              onChange={setDraft}
            />
          </div>

          <div className="mt-auto flex items-center gap-2 pt-3">
            <Button
              type="button"
              size="sm"
              disabled={!hasSelection || isSaving}
              onClick={() => setPending({ kind: 'preEvaluation' })}
            >
              Aplicar
            </Button>
          </div>
        </section>

        <MortgageAgentPromptsCard selectedIds={selectedIds} subProjects={subProjects} />
      </div>

      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}

      <MortgageConfirmDialog
        copy={confirmCopy}
        isLoading={isSaving}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          void handleConfirm();
        }}
      />
    </div>
  );
};

export default MortgageBulkActions;
